/**
 * V2 Backdrop Section Field Configuration
 *
 * V2 equivalent of backgroundSectionConfig.ts.
 * Maps V2 StepNode.content.canvas.background paths.
 *
 * V2 Backdrop schema (discriminated union):
 *   Backdrop = { kind: BackdropKind, content: SolidBackdrop | ImageBackdrop | ClipBackdrop }
 *   SolidBackdrop  = { color: string }
 *   ImageBackdrop  = { image: LocalizedImage }   → translations.{locale}: AssetReference
 *   ClipBackdrop   = { video: LocalizedClip }    → translations.{locale}: AssetReference
 *
 * V2 path changes from V1:
 * - styles.background.kind → content.canvas.background.kind
 * - kind values: 'color'→'solid', 'image'→'photo', 'video'→'clip'
 * - styles.background.styles.color → content.canvas.background.content.color
 * - styles.background.styles.image → content.canvas.background.content.image (LocalizedImage)
 * - styles.background.styles.video → content.canvas.background.content.video (LocalizedClip)
 *
 * Sprint: schema-v2-migration, Sprint 3
 */

import type { FieldConfig } from '../types/editorFieldTypes';

/**
 * V2 Backdrop Kind options (V2 uses different names from V1)
 */
export const V2_BACKDROP_KIND_OPTIONS = [
  { value: 'solid', label: 'Solid Color' },
  { value: 'photo', label: 'Photo' },
  { value: 'clip', label: 'Video Clip' },
] as const;

/**
 * V2 Backdrop kind type
 */
export type V2BackdropKind = 'solid' | 'photo' | 'clip';

/**
 * V2 Backdrop kind selector
 *
 * Path: content.canvas.background.kind
 */
export const V2_BACKDROP_KIND_CONFIG: FieldConfig = {
  path: 'content.canvas.background.kind',
  label: 'Type',
  type: 'select',
  options: [...V2_BACKDROP_KIND_OPTIONS],
  defaultValue: 'solid',
  isNullable: false,
  guideKey: 'screenedit-backdrop-type',
};

/**
 * V2 Backdrop color field (when kind='solid')
 *
 * Path: content.canvas.background.content.color (SolidBackdrop.color)
 */
export const V2_BACKDROP_COLOR_CONFIG: FieldConfig = {
  path: 'content.canvas.background.content.color',
  label: 'Color',
  type: 'color',
  defaultValue: '#ffffff',
  isNullable: false,
  guideKey: 'screenedit-backdrop-solid-color',
};

/**
 * V2 Backdrop image field (when kind='photo')
 *
 * Path: content.canvas.background.content.image (ImageBackdrop.image = LocalizedImage)
 * Asset URL: content.canvas.background.content.image.translations.{locale}.original
 */
export const V2_BACKDROP_IMAGE_CONFIG: FieldConfig = {
  path: 'content.canvas.background.content.image',
  label: 'Image URL',
  type: 'imageUpload',
  guideKey: 'screenedit-backdrop-photo',
};

/**
 * V2 Backdrop video field (when kind='clip')
 *
 * Path: content.canvas.background.content.video (ClipBackdrop.video = LocalizedClip)
 * Asset URL: content.canvas.background.content.video.translations.{locale}.original
 */
export const V2_BACKDROP_VIDEO_CONFIG: FieldConfig = {
  path: 'content.canvas.background.content.video',
  label: 'Video URL',
  type: 'videoUpload',
  guideKey: 'screenedit-backdrop-video',
};

/**
 * Per-screen browser chrome color (status bar, bottom bar).
 * Falls back to FlowDocument.webViewDefaultBackgroundColor when null.
 *
 * Path: content.canvas.webViewBackgroundColor
 */
export const V2_WEB_VIEW_BG_COLOR_CONFIG: FieldConfig = {
  path: 'content.canvas.webViewBackgroundColor',
  label: 'Browser chrome color',
  type: 'color',
  defaultValue: null,
  isNullable: true,
  guideKey: 'screenedit-backdrop-browser-chrome-color',
};
